import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* REST API */
import { profile, save, upload, remove } from "../../api/Profile"

/* Widgets */
import { Layout } from "../Table"

/* Components */
import { Input } from "../../components"

/* Icons */
import { CloseIcon, CameraIcon, TrashIcon, MailIcon } from "../../components/Icons"
import { LoadingOutlined } from "@ant-design/icons"

/* Helpers */
import { utils } from "../../helpers"

/* Constants */
import { env } from "../../constants"

/* Sweet Alert */
import Swal from "../../helpers/alert"

/* Translate */
import { t } from "../../translates/translate"

/* Moment */
import moment from "moment"
import "moment/locale/ru"


/* THE STATE OF A CLOSED PANEL — open() STARTS FROM IT EVERY TIME */
const EMPTY = {
    status: "loading",
    name: "",
    email: "",
    image: null,
    access: "",
    scope: "",
    studios: [],
    operators: [],
    counts: { studios: 0, operators: 0, tables: 0 },
    sessions: 0,
    device: null,
    createdAt: null,
    waiting: false,
    uploading: false,
}

/* THE NUMBER OF SCOPE NAMES SHOWN BEFORE THE REST TURNS INTO A COUNTER */
const NAMES = 6


/* Widget Profile */
class Profile extends Component {

    constructor() {
        super()

        this.state = { visible: false, ...EMPTY }
    }

    /* Open */
    open = () => {

        /* Open Modal */
        this.setState({ visible: true, ...EMPTY })

        /* Load Action */
        this.load()
    }

    /* Close */
    close = () => this.setState({ visible: false, ...EMPTY })


    /* Load Action */
    load = (reload = false) => {

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Load Profile */
        profile().then((response) => {
            if (response.status === 200) {

                /* Fields */
                const data = response.data

                /* Set Data */
                this.setState({
                    status: "ok",
                    name: utils.checkValue(data.name),
                    email: utils.checkValue(data.email),
                    image: utils.checkValue(data.image, null),
                    access: utils.checkValue(data.access),
                    scope: utils.checkValue(data.scope),
                    studios: utils.checkValue(data.studios, []),
                    operators: utils.checkValue(data.operators, []),
                    counts: utils.checkValue(data.counts, EMPTY.counts),
                    sessions: parseInt(utils.checkValue(data.sessions, 0)),
                    device: utils.checkValue(data.device, null),
                    createdAt: utils.checkValue(data.createdAt, null),
                })

            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission" })
            }
            else if (response.status === 498) {
                this.setState({ status: "key" })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => {
            this.setState({ status: "network" })
        })
    }


    /* KEEP THE STORED USER IN STEP — THE DASHBOARD GREETS BY THAT VERY NAME */
    store = (name) => {

        try {

            /* Fields */
            const json = localStorage.getItem("user")

            /* Check Storage */
            if (!json) {
                return
            }

            /* Fields */
            const user = JSON.parse(json)

            /* Save Name */
            localStorage.setItem("user", JSON.stringify({ ...user, name }))

        }
        catch {
            /* Skip */
        }
    }


    /* Save Action */
    save = () => {

        /* Fields */
        const { name, waiting } = this.state
        const { onUpdate } = this.props

        /* Fields */
        const value = name.trim()

        /* Check Waiting */
        if (waiting) {
            return
        }

        /* Check Name */
        if (value.length < 2) {
            this._name.error(t("profile.error.emptyName"))
            return
        }

        /* Start Saving */
        this.setState({ waiting: true })

        /* Save Name */
        save({ name: value }).then((response) => {
            if (response.status === 200) {

                /* Set Name */
                this.setState({ name: value })

                /* Store Name */
                this.store(value)

                /* Update Header */
                if (onUpdate) {
                    onUpdate()
                }

                Swal.fire(utils.notification(t("profile.savedSuccess"), "success"))
            }
            else if (response.status === 498) {
                Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })
    }


    /* Upload Action */
    uploadFile = (event) => {

        /* Fields */
        const file = event.target.files[0]
        const { onUpdate } = this.props

        /* Check File */
        if (!file) {
            return
        }

        /* Fields */
        const data = new FormData()
        data.append("image", file)

        /* Clear Input — the very same file may be picked again */
        event.target.value = ""

        /* Start Uploading */
        this.setState({ uploading: true })

        /* Upload Image */
        upload(data).then((response) => {
            if (response.status === 200) {

                /* Set Image */
                this.setState({ image: response.data.image })

                /* Update Header */
                if (onUpdate) {
                    onUpdate()
                }

                Swal.fire(utils.notification(t("profile.imageSaved"), "success"))
            }
            else if (response.status === 400) {
                Swal.fire(utils.notification(t("profile.error.image"), "error"))
            }
            else if (response.status === 498) {
                Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
            }
            else {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ uploading: false })
        })
    }


    /* Remove Action */
    removeFile = () => {

        /* Fields */
        const { uploading } = this.state
        const { onUpdate } = this.props

        /* Check Uploading */
        if (uploading) {
            return
        }

        /* Ask */
        Swal.fire({
            title: t("profile.removeImageTitle"),
            text: t("profile.removeImageText"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            confirmButtonText: t("common.delete"),
            cancelButtonText: t("common.cancel"),
        }).then((result) => {

            /* Check Answer */
            if (!result.isConfirmed) {
                return
            }

            /* Start Removing */
            this.setState({ uploading: true })

            /* Remove Image */
            remove().then((response) => {
                if (response.status === 200) {

                    /* Clear Image */
                    this.setState({ image: null })

                    /* Update Header */
                    if (onUpdate) {
                        onUpdate()
                    }

                    Swal.fire(utils.notification(t("profile.imageDeleted"), "success"))
                }
                else if (response.status === 498) {
                    Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                }
                else {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                }
            }).catch(() => {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            }).finally(() => {
                this.setState({ uploading: false })
            })

        })
    }


    /* INITIALS OF A PROFILE WITHOUT A PICTURE */
    initials = () => {

        /* Fields */
        const { name } = this.state

        /* Check Name */
        if (name.trim().length === 0) {
            return "?"
        }

        /* Send Result */
        return name.trim().split(" ").filter((item) => item).slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("")
    }


    /* Draw Avatar */
    _avatar = () => {

        /* Fields */
        const { image, uploading } = this.state

        return (
            <div className="profile--avatar">

                {/* Picture */}
                {image
                    ? <img src={`${env.mediapoint}${image}`} alt="Avatar" className="profile--avatar-image" />
                    : <div className="profile--avatar-initials">{this.initials()}</div>}

                {/* Loading */}
                {uploading && <div className="profile--avatar-loading"><LoadingOutlined /></div>}

                {/* Upload */}
                <label className="profile--avatar-action upload" title={t("profile.uploadImage")}>
                    <input onChange={(event) => this.uploadFile(event)} type="file" accept="image/png, image/jpeg, image/webp" />
                    <CameraIcon size={15} />
                </label>

                {/* Remove */}
                {image && (
                    <div onClick={() => this.removeFile()} className="profile--avatar-action remove" title={t("profile.removeImage")}>
                        <TrashIcon size={14} />
                    </div>
                )}

            </div>
        )
    }


    /* Draw Hero */
    _hero = () => {

        /* Fields */
        const { name, email, access } = this.state

        return (
            <div className="profile--hero">

                {/* Draw Avatar */}
                {this._avatar()}

                <div className="profile--hero-text">

                    <h3>{utils.checkValue(name, "—")}</h3>

                    <div className="profile--hero-email">
                        <MailIcon size={13} />
                        <span>{utils.checkValue(email, "—")}</span>
                    </div>

                    <div className={`profile--role ${access}`}>{t(`access.${utils.getRole({ access })}`)}</div>

                </div>

            </div>
        )
    }


    /* Draw Stats */
    _stats = () => {

        /* Fields */
        const { counts, sessions, createdAt } = this.state

        /* Fields */
        const list = [
            { value: counts.tables, label: t("profile.stats.games") },
            { value: counts.studios, label: t("profile.stats.studios") },
            { value: counts.operators, label: t("profile.stats.operators") },
            { value: sessions, label: t("profile.stats.sessions") },
        ]

        return (
            <div className="profile--stats">

                {list.map((item, index) =>
                    <div className="profile--stat" key={`${index}`}>
                        <b>{parseInt(utils.checkValue(item.value, 0))}</b>
                        <span>{item.label}</span>
                    </div>
                )}

                {createdAt && (
                    <div className="profile--stat wide">
                        <b>{moment(createdAt).format("D MMMM YYYY")}</b>
                        <span>{t("profile.stats.created")}</span>
                    </div>
                )}

            </div>
        )
    }


    /* Draw Scope — THE STUDIOS AND THE OPERATORS THE TOKEN REACHES */
    _scope = () => {

        /* Fields */
        const { scope, studios, operators } = this.state

        /* Check Scope — a global token reaches everything, a list of every studio says nothing */
        if (scope === "global") {
            return (
                <div className="profile--section">
                    <div className="profile--section-title">{t("profile.access")}</div>
                    <div className="profile--note">{t("profile.scope.global")}</div>
                </div>
            )
        }

        /* Fields */
        const groups = [
            { title: t("profile.stats.studios"), list: studios },
            { title: t("profile.stats.operators"), list: operators },
        ].filter((item) => item.list.length > 0)

        /* Check Groups */
        if (groups.length === 0) {
            return (
                <div className="profile--section">
                    <div className="profile--section-title">{t("profile.access")}</div>
                    <div className="profile--note">{t("profile.scope.empty")}</div>
                </div>
            )
        }

        return (
            <div className="profile--section">

                <div className="profile--section-title">{t("profile.access")}</div>

                {groups.map((group, index) =>
                    <div className="profile--group" key={`${index}`}>

                        <span className="profile--group-title">{group.title}</span>

                        <div className="profile--chips">
                            {group.list.slice(0, NAMES).map((item, key) => <span className="profile--chip" key={`${key}`}>{item}</span>)}
                            {group.list.length > NAMES && <span className="profile--chip more">{`+${group.list.length - NAMES}`}</span>}
                        </div>

                    </div>
                )}

            </div>
        )
    }


    /* Draw Device — THE SESSION THIS VERY TOKEN BELONGS TO */
    _device = () => {

        /* Fields */
        const { device } = this.state

        /* Check Device */
        if (!device) {
            return null
        }

        /* Fields */
        const place = [device.country, device.city].filter((item) => item).join(", ")

        return (
            <div className="profile--section">

                <div className="profile--section-title">{t("profile.currentSession")}</div>

                <div className="profile--device">

                    <div className="profile--device-row">
                        <span>{t("profile.browser")}</span>
                        <b>{utils.checkValue(device.browser, "—")}</b>
                    </div>

                    <div className="profile--device-row">
                        <span>{t("profile.ipAddress")}</span>
                        <b>{utils.checkValue(device.ip, "—")}</b>
                    </div>

                    <div className="profile--device-row">
                        <span>{t("profile.countryCity")}</span>
                        <b>{place === "" ? "—" : place}</b>
                    </div>

                    <div className="profile--device-row">
                        <span>{t("profile.lastActivity")}</span>
                        <b>{device.updatedAt ? moment(device.updatedAt).format("D MMMM YYYY, HH:mm") : "—"}</b>
                    </div>

                </div>

            </div>
        )
    }


    /* Draw Button */
    _button = () => {

        /* Fields */
        const { waiting } = this.state

        /* Waiting */
        if (waiting) {
            return <div className="profile--button loading"><LoadingOutlined /></div>
        }

        return <div onClick={() => this.save()} className="profile--button">{t("common.save")}</div>
    }


    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status, name, email } = this.state

        /* Layout */
        if (status !== "ok") {
            return (
                <div className="profile--layout">
                    <Layout status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="profile--body">

                {/* Draw Hero */}
                {this._hero()}

                {/* Draw Stats */}
                {this._stats()}

                {/* Form */}
                <div className="profile--section">

                    <div className="profile--section-title">{t("profile.data")}</div>

                    <Input
                        ref={(ref) => this._name = ref}
                        label={t("profile.name")}
                        value={name}
                        onChange={(event) => this.setState({ name: event.target.value })}
                    />

                    <Input label="Email" value={email} disabled />

                </div>

                {/* Draw Scope */}
                {this._scope()}

                {/* Draw Device */}
                {this._device()}

            </div>
        )
    }


    render = () => {

        /* Fields */
        const { visible, status } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="profile--modal"
                overlayClassName="profile--overlay"
                closeTimeoutMS={220}
            >

                <div className="profile--header">

                    <div className="profile--header-text">
                        <h2>{t("profile.title")}</h2>
                        <span>{t("profile.subtitle")}</span>
                    </div>

                    <div onClick={() => this.close()} className="profile--close">
                        <CloseIcon size={13} />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {status === "ok" && (
                    <div className="profile--footer">
                        <div onClick={() => this.close()} className="profile--button ghost">{t("common.close")}</div>
                        {this._button()}
                    </div>
                )}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(Profile)
